import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import ProductCard, {
  ProductCardActionType
} from '@/components/organisms/productCard/productCard'
import { Box, Grid } from '@/components/atoms/Grid'
import { BridgeProduct } from '@/types/bridgeProduct'
import Spinner, { SpinnerSize } from '@/components/molecules/spinner'
import styled from 'styled-components'
import {
  FilterInput,
  HomeBanner,
  ListingBanner,
  Product
} from '@/network/graphql.g'
import { ListingData } from '@/types/listingData'
import { useRouter } from 'next/router'
import { Config } from '@/core/config'
import useSWR, { Arguments } from 'swr'
import { makeFilterInputFromQuery } from '@/providers/filterProvider/parsingFilters'
import { ProductCardMode } from '@/components/organisms/productCard/productCardMode'
import isEqual from 'lodash.isequal'
import ProductsListingExpander from './productsListingExpander'
import Link from '@/components/atoms/link'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { ProductListingFetcher } from '@/components/organisms/productListing/productListingFetcher'
import { GetServerSidePropsContext } from 'next'
import {
  createUrl,
  getPageFromPathOrDefault,
  getPageFromRouter,
  getPageFromRouterOrDefault
} from '@/utils/urlHelper'
import ProductsListingContentPlaceholder from './productListingContentPlaceholder'
import { Redirect } from 'next'
import { createFilterId } from 'hooks/useProductListing'
import { ProductCardColors } from '@/components/atoms/productCardPlaceholder'
import { validFor } from '@/theme/validFor'
import PagerNew from '@/components/molecules/pagerNew'
import Filter from '@/components/organisms/filter'
import { Routes } from '@/core/routes'
import useTranslation from 'next-translate/useTranslation'
import CampaignCounter from '@/components/molecules/campaignCounter'
import CategoryProductBanner from '@/components/organisms/categoryProductBanner'
import { PreloadMode } from '@/components/organisms/bannersLayout'
import ProductListingPathHeaderRedesign from '@/components/molecules/productListingPathHeaderRedesign'
import ProductListingTitleCountInfoRedesign from '@/components/molecules/productListingTitleCountInfoRedesign'
import { Desktop, MediaContextProvider } from '@/components/atoms/media'
import ProductListingDescription from '@/components/molecules/productListingDescription'

type ProductsContainerResponsiveData<T> = {
  wide?: T
  desktop: T
  tablet: T
  mobile: T
}

type ProductListingProps = {
  listData: ListingData<BridgeProduct>
  itemsPerRow: number
  listingBannerRowProperty: string
  listingBannerSizeProperty: string
  listingBannerImageProperty: string
  fetcher: ProductListingFetcher
  maxWidth: ProductsContainerResponsiveData<number | string>
  list: string
  colors?: ProductCardColors
  indentText?: boolean
  mode?: ProductCardMode
  action: ProductCardActionType
  useFitIn?: boolean
  isInSaleCampaign?: boolean
  isParfumCampaign?: boolean
  campaignTitle?: string
  campaignId?: string
  isCampaignForCountDown?: boolean
  isCampaign?: boolean
  categoryProductBanners?: HomeBanner[]
  desktopImageUrl?: string
  mobileImageUrl?: string
  description?: string
}

enum ListingBannerPosition {
  Left = 1,
  Right = 2
}

class InsertItem {
  constructor(
    public position: number,
    public columnIndex: number,
    public data: Product
  ) {}
}

type ListingBannerProps = {
  sizeProperty: string
  imageProperty: string
  rowProperty: string
  items: ListingBanner[]
}

type ListState = {
  key: Arguments
  items: Product[][]
}

const PreloadedProductsCount = 4

const ProductListingContent: React.FC<{
  data: Product[][]
  dataKey: Arguments
  maxWidth: ProductsContainerResponsiveData<number | string>
  action: ProductCardActionType
  itemsPerRow: number
  listingBanner: ListingBannerProps
  initialPage: number
  disableFetch: () => void
  list: string
  colors: ProductCardColors
  indentText: boolean
  mode: ProductCardMode
  mainCategoryId: string
  useFitIn?: boolean
}> = ({
  data,
  maxWidth,
  action,
  itemsPerRow,
  listingBanner,
  initialPage,
  disableFetch,
  list,
  colors,
  indentText,
  mainCategoryId,
  mode = ProductCardMode.productList,
  useFitIn = false
}) => {
  const [insertPosition, setInsertPosition] = useState<InsertItem>(null)

  useEffect(() => {
    if (data.length === 1) {
      setInsertPosition(null)
    }
  }, [data])

  const allProducts = useMemo(() => data?.flatMap((items) => items), [data])

  let productPosition = 0
  let productListingBannerPosition = 0

  const getExpander = () => (
    <StyledExpander
      columnCount={itemsPerRow}
      key={`${insertPosition.position}-${insertPosition.data?.id}-${itemsPerRow}`}
    >
      <ProductsListingExpander
        columnCount={itemsPerRow}
        index={insertPosition.position}
        indicatorIndex={insertPosition.columnIndex}
        action={action}
        productId={insertPosition.data?.id}
        onClose={() => {
          setInsertPosition(null)
        }}
      />
    </StyledExpander>
  )

  const getListingBanner = (banner: ListingBanner, offset: number) => {
    const image = (
      <StyledResponsiveImage
        imageUrl={banner.image[listingBanner.imageProperty]}
        layout="fill"
        objectFit="contain"
        objectPosition="top"
      />
    )
    return (
      <Fragment key={offset}>
        <Grid
          gridColumn={`span ${banner[listingBanner.sizeProperty]}`}
          mb={64}
          position="relative"
          pt={`${
            (Config.imageSizes.xl.height /
              Config.imageSizes.xl.width /
              banner[listingBanner.sizeProperty]) *
            100
          }%`}
        >
          {banner.url ? (
            banner.url.startsWith('http') ? (
              <a href={banner.url}>{image}</a>
            ) : (
              <Link href={banner.url}>{image}</Link>
            )
          ) : (
            image
          )}
        </Grid>
      </Fragment>
    )
  }

  const getCurrentListingBanner = (row: number, position: number) => {
    const rowPosition = (position - 1) % itemsPerRow
    const getListingBannerPosition = (banner: ListingBanner) =>
      banner.position === ListingBannerPosition.Left
        ? 0
        : itemsPerRow - banner[listingBanner.sizeProperty]
    return (
      validListingBanners?.find(
        (item) =>
          item[listingBanner?.rowProperty] === row &&
          getListingBannerPosition(item) === rowPosition
      ) ?? null
    )
  }

  const validListingBanners = useMemo(
    () =>
      (
        listingBanner?.items?.filter(
          (item) =>
            item.image?.[listingBanner?.imageProperty] &&
            item?.[listingBanner?.sizeProperty]
        ) ?? []
      ).sort((a, b) => {
        if (a[listingBanner?.rowProperty] < b[listingBanner?.rowProperty]) {
          return -1
        }
        if (a[listingBanner?.rowProperty] > b[listingBanner?.rowProperty]) {
          return 1
        }
        return a.position - b.position
      }),
    [listingBanner]
  )

  return (
    <Box>
      {allProducts && (
        <StyledGrid
          maxWidth={maxWidth}
          gridTemplateColumns={`repeat(${itemsPerRow}, 1fr)`}
          gridRowGap={'30px'}
          gridColumnGap={{ desktop: 20, tablet: 20, mobile: 10 }}
        >
          {allProducts
            ?.filter((item) => item)
            ?.map((item, offset) => {
              const page =
                Math.floor(productPosition / Config.perPage) + initialPage
              const row =
                1 +
                Math.floor(productListingBannerPosition / itemsPerRow) +
                ((initialPage - 1) * Config.perPage) / itemsPerRow
              productPosition++
              productListingBannerPosition++
              const currentListingBanner = getCurrentListingBanner(
                row,
                productListingBannerPosition
              )
              let listingBannerElement: JSX.Element = null
              const prevProductListingBannerPosition =
                productListingBannerPosition
              if (currentListingBanner) {
                listingBannerElement = getListingBanner(
                  currentListingBanner,
                  offset
                )
                productListingBannerPosition +=
                  currentListingBanner[listingBanner.sizeProperty]
              }
              let beforeExpander: JSX.Element = null
              let afterExpander: JSX.Element = null
              if (
                insertPosition &&
                insertPosition.position === prevProductListingBannerPosition - 1
              ) {
                beforeExpander = getExpander()
              }
              if (
                insertPosition &&
                insertPosition.position >
                  prevProductListingBannerPosition - 1 &&
                insertPosition.position <= productListingBannerPosition - 1
              ) {
                afterExpander = getExpander()
              }

              return (
                <Fragment key={item?.id}>
                  {beforeExpander}
                  {listingBannerElement && listingBannerElement}
                  {afterExpander}
                  <ProductCard
                    product={item}
                    virtualScrolling={!(item as BridgeProduct)?.preload}
                    onAddToCart={action}
                    position={productPosition}
                    page={page}
                    mode={mode}
                    disableFetch={disableFetch}
                    list={list}
                    colors={colors}
                    indentText={indentText}
                    mainCategoryId={mainCategoryId}
                    useFitIn={useFitIn}
                  />
                  {insertPosition &&
                    offset === allProducts.length - 1 &&
                    insertPosition.position >= productListingBannerPosition &&
                    getExpander()}
                </Fragment>
              )
            })}
        </StyledGrid>
      )}
    </Box>
  )
}
;(prev, next) => isEqual(prev.dataKey, next.dataKey)

const ProductListing: React.FC<ProductListingProps> = ({
  action,
  listData,
  itemsPerRow,
  fetcher,
  listingBannerRowProperty,
  listingBannerSizeProperty,
  listingBannerImageProperty,
  maxWidth,
  list,
  colors,
  indentText,
  mode,
  useFitIn = false,
  isParfumCampaign,
  isInSaleCampaign,
  campaignTitle,
  campaignId,
  isCampaignForCountDown,
  categoryProductBanners,
  desktopImageUrl,
  mobileImageUrl,
  isCampaign,
  description
}) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [totalPagesCount, setTotalPagesCount] = useState<number>(
    listData.isPreload ? null : listData.totalPagesCount
  )

  const handleNextProducts = (nextProducts: Product[]): Product[] => {
    return nextProducts
  }

  const getRouterAsPath = () => {
    const asPathUrl = createUrl(router.asPath)
    asPathUrl.searchParams.delete('na')
    return `${asPathUrl.pathname}${asPathUrl.search}${asPathUrl.hash}`
  }

  const prevPath = useRef<string>(getRouterAsPath())
  const prevPathFetch = useRef<string>(getRouterAsPath())

  const canFetch = useRef<boolean>(true)
  const canAutoFetch = useRef<boolean>(false)

  const disableFetch = () => {
    canFetch.current = false
  }

  const getKey = (): Arguments => {
    if (!canFetch.current) {
      return null
    }
    const asPath = getRouterAsPath()
    let nextPage = page
    if (prevPath.current !== asPath) {
      nextPage = getPageFromRouterOrDefault(router)
      setPage(nextPage)
    }
    prevPath.current = asPath

    if (nextPage - 1 > (totalPagesCount ?? 0) && totalPagesCount !== null) {
      return null
    }
    return [nextPage, asPath]
  }
  const swrFetcher = async (
    index: number,
    path: string
  ): Promise<Product[]> => {
    const prevPage = getPageFromPathOrDefault(prevPathFetch.current)
    const asPath = getRouterAsPath()
    const pageClicked = prevPathFetch.current !== asPath && prevPage !== index
    prevPathFetch.current = asPath

    if (index < (parseInt(getPageFromRouter(router)) || 0)) {
      return []
    }
    const filterInput = makeFilterInputFromQuery(router.query)

    const refresh = index === getPageFromRouterOrDefault(router)

    const data = await fetcher(index, filterInput, refresh)

    if (data.totalPagesCount != null) {
      setTotalPagesCount(data.totalPagesCount)
    }
    const nextProducts = handleNextProducts(data.products)

    setData((data) => {
      if (index > data.items.length && !pageClicked) {
        return {
          key: [index, path],
          items: [...data.items, nextProducts]
        }
      } else {
        return {
          key: [index, path],
          items: [nextProducts]
        }
      }
    })

    return []
  }

  const [page, setPage] = useState<number>(getPageFromRouterOrDefault(router))

  const makeListData = (): ListState => {
    const key = getKey()
    if (!key || key.length < 2) {
      return undefined
    }
    return {
      key: [key[0], `${key[1]}-${listData?.products?.length ?? 0}`],
      items: [
        handleNextProducts(
          listData.products?.map((item, index) => ({
            ...item,
            ...{ preload: index < PreloadedProductsCount }
          }))
        )
      ]
    }
  }

  const [data, setData] = useState<ListState>(makeListData)
  const initialProducts = useRef(listData?.products)

  useEffect(() => {
    const newData = makeListData()
    if (newData) {
      if (listData?.products !== initialProducts.current) {
        setData(newData)
      }
      if (!listData.isPreload || listData.totalPagesCount === 0) {
        setTotalPagesCount(listData.totalPagesCount)
      }
    }
  }, [listData?.products])

  const { isValidating } = useSWR(getKey, swrFetcher, {
    revalidateOnFocus: false,
    revalidateOnMount: false,
    errorRetryCount: 1
  })

  const hasItems = useMemo(
    () => (data?.items?.flatMap((items) => items)?.length ?? 0) > 0,
    [data]
  )

  useEffect(() => {
    const handler = setTimeout(() => {
      canAutoFetch.current = true
    }, 1000)
    return () => {
      clearTimeout(handler)
    }
  }, [])

  const listingBannerProps: ListingBannerProps = useMemo(
    () => ({
      sizeProperty: listingBannerSizeProperty,
      imageProperty: listingBannerImageProperty,
      rowProperty: listingBannerRowProperty,
      items: listData?.listingBanners
    }),
    [
      listingBannerSizeProperty,
      listingBannerImageProperty,
      listingBannerRowProperty,
      listData?.listingBanners
    ]
  )

  return (
    <>
      {isValidating && <StyledSpinner />}
      <CategoryProductBanner
        banners={
          isCampaignForCountDown
            ? categoryProductBanners.map((banner) => ({
                ...banner,
                imageBig: desktopImageUrl,
                imageMobile: mobileImageUrl,
                url1: '',
                url2: ''
              }))
            : categoryProductBanners
        }
        preload
        preloadMode={PreloadMode.First}
        rightContent={isCampaignForCountDown ? <CampaignCounter /> : null}
      />
      {isCampaign && campaignTitle && (
        <MediaContextProvider>
          <Desktop>
            <ProductListingPathHeaderRedesign
              title={campaignTitle}
              paths={[
                {
                  url: `${Routes.campaign}`,
                  title: t('Campaign.title')
                },
                ...(campaignId
                  ? [
                      {
                        url: `${Routes.campaign}${campaignId}`,
                        title: campaignTitle
                      }
                    ]
                  : [])
              ]}
            />
          </Desktop>
        </MediaContextProvider>
      )}
      {isCampaign && campaignTitle && (
        <ProductListingTitleCountInfoRedesign title={campaignTitle} />
      )}
      <Filter
        isInSaleCampaign={isInSaleCampaign}
        isParfumCampaign={isParfumCampaign}
      />
      {!hasItems && totalPagesCount !== null && data !== undefined && (
        <ProductsListingContentPlaceholder />
      )}
      {hasItems && (
        <ProductListingContent
          dataKey={data.key}
          data={data.items}
          maxWidth={maxWidth}
          action={action}
          itemsPerRow={itemsPerRow}
          listingBanner={listingBannerProps}
          initialPage={getPageFromRouterOrDefault(router)}
          disableFetch={disableFetch}
          list={list}
          colors={colors}
          indentText={indentText}
          mode={mode}
          mainCategoryId={listData?.id}
          useFitIn={useFitIn}
        />
      )}
      <PagerNew
        key={`pager-${totalPagesCount ?? 0}`}
        onClick={setPage}
        numberOfPages={totalPagesCount ?? 0}
        currentPageIndex={
          totalPagesCount === null ? page : Math.min(page, totalPagesCount)
        }
      />
      {hasItems && description && (
        <ProductListingDescription
          description={description}
          enableReadMore={true}
        />
      )}
    </>
  )
}

export default ProductListing

export const getProductListingData = (
  context: GetServerSidePropsContext
):
  | {
      redirect: Redirect
    }
  | {
      page: number
      limit: number
      filterInput: FilterInput[]
      filterId: string
      requestId: string
      isPreload: boolean
    } => {
  const referer = createUrl(context.req?.headers?.referer)
  const resolvedUrl = createUrl(context.resolvedUrl)
  if (
    referer?.searchParams?.get('a') &&
    !resolvedUrl?.searchParams?.get('na')
  ) {
    // back from product detail
    referer.hash = referer?.searchParams?.get('a')
    referer.searchParams.delete('a')
    referer.searchParams.append('na', '1')
    return {
      redirect: {
        destination: `${referer.pathname}${referer.search}${referer.hash}`,
        permanent: false
      }
    }
  }

  const limit = resolvedUrl?.searchParams?.get('na')
    ? Config.perPage
    : Config.perInitialPage
  const basePage = +((context.query?.page as string) ?? '1')
  let page = basePage
  const isPreload = limit === Config.perInitialPage
  if (isPreload) {
    page = ((page - 1) * Config.perPage) / Config.perInitialPage + 1
  }
  const filterInput = makeFilterInputFromQuery(context.query)
  return {
    page,
    limit,
    filterInput,
    filterId: createFilterId(filterInput, basePage),
    requestId: Date.now().toString(),
    isPreload
  }
}

const position = (size) => `calc(50% - ${size / 2}px)`
const positionTop = (size) => `calc(30% - ${size / 2}px)`

const StyledSpinner = styled(Spinner)`
  position: fixed;
  top: ${positionTop(SpinnerSize)};
  left: ${position(SpinnerSize)};
  z-index: 1000;
`

const StyledGrid = styled(Grid)`
  margin-top: 40px;
  flex: 1;
  ${validFor.mobile`
    margin-top: 20px;
  `}
`

const StyledExpander = styled(Box)`
  box-sizing: border-box;
  overflow: hidden;
  width: 100%;
  grid-column-start: span ${(props) => props.columnCount};
  grid-column-end: span 1;
`

const StyledResponsiveImage = styled(ResponsiveImage)`
  border-radius: ${(props) => props.theme.borderRadius}px;
`
